import { Link, useParams } from 'react-router-dom';
import { ScenarioCustomDataPanel } from '../components/ScenarioCustomDataPanel.js';
import { ScenarioNotesPanel } from '../components/ScenarioNotesPanel.js';
import { useScenarioDetail } from '../hooks.js';

/**
 * Scenario detail. Phase B real implementation will surface tabs
 * (overview / executions / KPI rollup / notes / AI summary). Today
 * dumps the engine response so the read path is exercised.
 */
export function ScenarioDetailPage(): JSX.Element {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const { data, isLoading, error } = useScenarioDetail(scenarioId);

  // The name is Fusion's (the sync overwrites it), so it is not editable here.
  const currentName = (data?.name as string | undefined) ?? '';

  return (
    <div className="mx-auto max-w-4xl p-8">
      <Link
        to="/scenarios"
        className="mb-3 inline-block text-xs text-blue-600 hover:underline dark:text-blue-400"
      >
        ← Back to list
      </Link>
      <div className="mb-1 flex items-center gap-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
          {currentName ? (
            currentName
          ) : (
            <>
              Scenario <span className="font-mono text-base text-gray-500">{scenarioId}</span>
            </>
          )}
        </h1>
      </div>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Per-scenario engine snapshot. Notes panel below; tabbed surface arrives in Phase B.
      </p>
      {isLoading ? (
        <div className="rounded-md border border-gray-200 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
          Loading…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          <p className="mb-1 font-semibold">Could not load scenario</p>
          <p className="text-xs">{error.message}</p>
        </div>
      ) : (
        <pre className="overflow-x-auto rounded-md border border-gray-200 bg-gray-50 p-4 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
      {scenarioId ? (
        <>
          <ScenarioCustomDataPanel
            scenarioId={scenarioId}
            initialCustomData={data ? extractCustomData(data) : null}
          />
          <ScenarioNotesPanel scenarioId={scenarioId} />
        </>
      ) : null}
    </div>
  );
}

function extractCustomData(scenario: Record<string, unknown>): unknown {
  // Engine response shape varies — try common keys.
  return (
    (scenario.customData as unknown) ??
    (scenario.custom_data as unknown) ??
    (scenario.customFieldsData as unknown) ??
    null
  );
}
